// ============================================================================
// Beacon — saved growth scenarios (name + compare).
// Same dual-mode repo pattern as deals: cloud (Supabase `growth_scenarios`)
// with a graceful local fallback, so it works before the table is created.
// ============================================================================

import type { DealTemplate } from "./growth";
import { getSupabase, isCloud } from "./supabase";

export interface Scenario {
  id: string;
  name: string;
  template: DealTemplate;
  horizon: number;
}

let counter = 0;
export function newScenarioId(): string {
  counter += 1;
  return "sc" + Date.now().toString(36) + counter.toString(36);
}

const LS = "beacon_scenarios";
function localAll(): Scenario[] {
  try {
    const raw = localStorage.getItem(LS);
    if (raw) return JSON.parse(raw) as Scenario[];
  } catch { /* ignore */ }
  return [];
}
function localSave(all: Scenario[]) {
  try { localStorage.setItem(LS, JSON.stringify(all)); } catch { /* ignore */ }
}

export async function listScenarios(): Promise<Scenario[]> {
  if (isCloud()) {
    try {
      const sb = getSupabase()!;
      const { data, error } = await sb.from("growth_scenarios").select("*").order("created_at");
      if (error) throw error;
      if (data) return data.map((r: { id: string; name: string; payload: { template: DealTemplate; horizon: number } }) => ({ id: r.id, name: r.name, template: r.payload.template, horizon: r.payload.horizon }));
    } catch { /* fall through to local */ }
  }
  return localAll();
}

export async function saveScenario(s: Scenario): Promise<void> {
  if (isCloud()) {
    try {
      const sb = getSupabase()!;
      const { error } = await sb.from("growth_scenarios").upsert({ id: s.id, name: s.name, payload: { template: s.template, horizon: s.horizon } });
      if (error) throw error;
      return;
    } catch { /* fall through */ }
  }
  const all = localAll();
  const i = all.findIndex((x) => x.id === s.id);
  if (i >= 0) all[i] = s; else all.push(s);
  localSave(all);
}

export async function deleteScenario(id: string): Promise<void> {
  if (isCloud()) {
    try {
      const sb = getSupabase()!;
      const { error } = await sb.from("growth_scenarios").delete().eq("id", id);
      if (error) throw error;
      return;
    } catch { /* fall through */ }
  }
  localSave(localAll().filter((x) => x.id !== id));
}
